'use client';

import React, { type ReactNode, createContext, useEffect, useMemo, useState } from 'react';

import { useTranslation } from '@/i18n/client';

export type RegionType = 'cn' | 'intl';
export type ThemeType = 'light' | 'dark';

export type AppEnvContextValue = {
  region: RegionType;
  setRegion: (r: RegionType) => void;
  theme: ThemeType;
  setTheme: (t: ThemeType) => void;
  toggleTheme: () => void;
};

export const AppEnvContext = createContext<AppEnvContextValue | null>(null);

export function AppEnvProvider({
  children,
  initialRegion = 'intl',
  initialTheme = 'light'
}: {
  children: ReactNode;
  initialRegion?: RegionType;
  initialTheme?: ThemeType;
}) {
  const [region, setRegion] = useState<RegionType>(initialRegion);
  const [theme, setThemeState] = useState<ThemeType>(initialTheme);

  const { i18n } = useTranslation();

  useEffect(() => {
    // 这里一定需要调用一下useTranslation 要不然后面use-model的时候调用到useTranslation的时候会直接报错
    console.log('i18n init');
  }, [i18n]);

  const setTheme = (t: ThemeType) => {
    setThemeState(t);

    try {
      // 同步到 DOM / localStorage / cookie
      const root = document.documentElement;

      if (t === 'dark') root.classList.add('dark');
      else root.classList.remove('dark');

      localStorage.setItem('theme', t);
      document.cookie = `theme=${t}; path=/; SameSite=Lax`;
    } catch {}
  };

  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  // region 也顺手落 cookie，方便后续请求/SSR 兜底
  const setRegionSafe = (r: RegionType) => {
    setRegion(r);

    try {
      document.cookie = `region=${r}; path=/; SameSite=Lax`;
    } catch {}
  };

  const value = useMemo<AppEnvContextValue>(
    () => ({ region, setRegion: setRegionSafe, theme, setTheme, toggleTheme }),
    [region, theme]
  );

  return <AppEnvContext.Provider value={value}>{children}</AppEnvContext.Provider>;
}
